import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import useSentry from 'Pods/Shared/Hooks/useSentry'

import { VALIDATION_RULES } from '../constants'

const sanitizeInput = (input) => {
  return input.trim().replace(/[<>]/g, '')
}

const validateInput = (input, type, isNegative = false) => {
  const sanitized = sanitizeInput(input)

  let minLength
  let typeName

  if (type === 'comment') {
    minLength = VALIDATION_RULES.COMMENT_MIN_LENGTH
    typeName = 'Comment'
  } else if (type === 'review') {
    if (isNegative) {
      minLength = VALIDATION_RULES.NEGATIVE_REVIEW_MIN_LENGTH
      typeName = 'Negative review'
    } else {
      minLength = VALIDATION_RULES.POSITIVE_REVIEW_MIN_LENGTH
      typeName = 'Positive review'
    }
  } else {
    minLength = VALIDATION_RULES.COMMENT_MIN_LENGTH
    typeName = 'Comment'
  }

  if (sanitized.length < minLength) {
    const errorMessage = minLength === 0
      ? null // No minimum length error for positive reviews
      : `${typeName} must be at least ${minLength} character${minLength === 1 ? '' : 's'} long`

    return {
      isValid: minLength === 0 || sanitized.length >= minLength,
      error: errorMessage,
      sanitized,
    }
  }

  if (sanitized.length > VALIDATION_RULES.MAX_LENGTH) {
    return {
      isValid: false,
      error: `${typeName} must not exceed ${VALIDATION_RULES.MAX_LENGTH} characters`,
      sanitized,
    }
  }

  return {
    isValid: true,
    error: null,
    sanitized,
  }
}

export default function useReviewActions({
  userName,
  handleAdd,
  handleRemove,
  setError,
  setIsSubmitting,
  resetForm,
}) {
  const { postData } = useApiPost()
  const pageConfig = usePageConfig()
  const { captureException } = useSentry()

  const getUserAvatar = () => {
    const avatarUrl = pageConfig?.currentUserAvatarUrl
    if (!avatarUrl) return null

    return {
      small: {
        url: avatarUrl,
      },
      url: avatarUrl,
    }
  }


  const createTempReview = (tempId, content, type, isNegative, hideUsername) => {
    const avatar = hideUsername ? null : getUserAvatar()

    const baseReview = {
      id: tempId,
      type,
      content,
      username: hideUsername ? 'Anonymous Buyer' : userName,
      anonymous: !!hideUsername,
      avatar,
      userRole: pageConfig?.product?.role,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }

    if (type === 'review') {
      // Reviews have a score property
      return {
        ...baseReview,
        score: isNegative ? -1 : 1,
      }
    }

    // Comments don't have a score property (or it's undefined)
    return baseReview
  }

  const handleError = (tempId, submitError, reviewType) => {
    handleRemove(tempId, reviewType)

    captureException(submitError, {
      tags: {
        feature: 'reviews',
        action: 'review_submit',
      },
      extra: {
        reviewType,
        itemId: pageConfig?.product?.id,
      },
    })

    setError(`Failed to post ${reviewType}. Please try again.`)
    setIsSubmitting(false)
  }

  const handleCommentPost = (comment, isNegative, hideUsername) => {
    const validation = validateInput(comment, 'comment', isNegative)

    if (!validation.isValid) {
      setError(validation.error)
      return
    }

    const itemId = pageConfig?.product?.id
    if (!itemId) {
      setError('Item ID not available')
      return
    }

    const tempId = Date.now()
    const tempReview = createTempReview(tempId, validation.sanitized, 'comment', isNegative, hideUsername)

    setIsSubmitting(true)
    setError(null)

    // Add optimistic review immediately
    handleAdd(tempReview)

    // Clear form immediately for optimistic UX
    resetForm()

    const formData = new URLSearchParams()
    formData.append('commentable_type', 'Item')
    formData.append('commentable_id', itemId.toString())
    formData.append('content', validation.sanitized)

    postData({
      path: '/api/internal/comments.html',
      data: formData,
      onSuccess: () => {
        setIsSubmitting(false)
      },
      onError: (error) => {
        handleError(tempId, error, 'comment')
      },
    })
  }

  const handleReviewPost = (comment, isNegative, hideUsername) => {
    const validation = validateInput(comment, 'review', isNegative)

    if (!validation.isValid) {
      setError(validation.error)
      return
    }

    const itemId = pageConfig?.product?.id
    if (!itemId) {
      setError('Item ID not available')
      return
    }

    const tempId = Date.now()
    const tempReview = createTempReview(tempId, validation.sanitized, 'review', isNegative, hideUsername)

    setIsSubmitting(true)
    setError(null)

    // Add optimistic review immediately
    handleAdd(tempReview)

    // Clear form immediately for optimistic UX
    resetForm()

    const score = isNegative ? -1 : 1
    const reviewPayload = {
      data: {
        attributes: {
          comment: validation.sanitized,
          score: score.toString(),
          item_id: itemId,
          anonymous: hideUsername,
        },
      },
    }

    postData({
      path: `/api/internal/items/${itemId}/rate`,
      data: reviewPayload,
      onSuccess: () => {
        setIsSubmitting(false)
      },
      onError: (error) => {
        handleError(tempId, error, 'review')
      },
    })
  }

  return {
    handleCommentPost,
    handleReviewPost,
  }
}
